/**
 * Whether a map may be opened by someone other than this account.
 *
 * Used before writing into a map something that only makes sense for this
 * account: linking one of its maps (main.js asks first), pointing links at
 * its own recovered copies (crdt-bridge.js applyStubMigrations, which then
 * leaves the map alone). Errs on "yes" when the server cannot say.
 */

/**
 * @param {string} docId
 * @param {{getIndex?: () => {docs?: Array<Object>}}} vault - VaultStorage
 * @param {(input: string, init?: Object) => Promise<Response>} [fetchFn]
 * @returns {Promise<boolean>}
 */
export async function mayHaveOtherMembers(docId, vault, fetchFn = (...args) => fetch(...args)) {
  const entry = vault?.getIndex?.()?.docs?.find((d) => d.docId === docId);
  if (!entry || entry._storage === "local") return false;
  if (entry.role && entry.role !== "owner") return true;
  try {
    const resp = await fetchFn(`/api/shares/${encodeURIComponent(docId)}/permissions`, { credentials: "include" });
    // Never registered online (no key row yet): not shared.
    if (resp.status === 403 || resp.status === 404) return false;
    if (!resp.ok) return true;
    const perms = await resp.json();
    // This account owns the map: any other live member is someone else.
    return Array.isArray(perms) && perms.some((p) => p.role !== "owner" && !p.is_revoked);
  } catch {
    return true;
  }
}
